// src/pages/admin/PaymentsDashboard.tsx
import React, { useState, useEffect } from 'react';
import { db } from '../../../firebase'; // Adjust path as needed
import { collection, getDocs, doc, DocumentData } from 'firebase/firestore';
import styled, { keyframes, css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faClock, faSearch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// --- Interfaces (Based on your Screenshot) ---
interface PaymentRecord extends DocumentData {
  id: string;
  amount: number;
  confirmation_code: string;
  created_date: string; // ISO string format is common
  currency: 'UGX';
  merchant_reference: string;
  order_tracking_id: string;
  payment_account: string;
  payment_method: string;
  payment_status: 'Completed' | 'Failed' | 'Pending';
  status_code: number;
  verified_at: string;
}

// --- Styled Components ---

const fadeAndSlide = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const DashboardContainer = styled.div`
  padding: 20px;
  animation: ${fadeAndSlide} 0.5s ease-out;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 10px;
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
`;

const TableWrapper = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th, td {
    padding: 12px 15px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background-color: #f8f9fa;
    color: #555;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background-color: #f5f5f5;
  }
`;

const StatusBadge = styled.span<{ status: PaymentRecord['payment_status'] }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;

  ${({ status }) => {
    switch (status) {
      case 'Completed':
        return css`
          background-color: #d4edda;
          color: #155724;
        `;
      case 'Failed':
        return css`
          background-color: #f8d7da;
          color: #721c24;
        `;
      case 'Pending':
        return css`
          background-color: #fff3cd;
          color: #856404;
        `;
      default:
        return css`
          background-color: #e2e3e5;
          color: #383d41;
        `;
    }
  }}
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 1rem;

  &:hover {
    color: #0056b3;
  }
`;

// --- Modal/Detail Styles (Simplified) ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  max-width: 600px;
  width: 90%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const DetailItem = styled.p`
  margin: 8px 0;
  font-size: 0.95rem;

  strong {
    display: inline-block;
    min-width: 150px;
    color: #555;
  }
`;

// src/pages/admin/PaymentsDashboard.tsx (Continued)

export const PaymentsDashboard: React.FC = () => {
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecord | null>(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const querySnapshot = await getDocs(collection(db, 'payments'));
        const paymentsList: PaymentRecord[] = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        } as PaymentRecord));
        setPayments(paymentsList);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching payments:", err);
        setError("Failed to load payment data. Check console for details.");
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const getStatusIcon = (status: PaymentRecord['payment_status']) => {
    switch (status) {
      case 'Completed': return faCheckCircle;
      case 'Failed': return faTimesCircle;
      case 'Pending': return faClock;
      default: return faInfoCircle;
    }
  };
  
  const formatDate = (dateString: string) => {
      // Simple date formatting helper
      try {
          const date = new Date(dateString.replace(' ', 'T').replace(':', ':'));
          return date.toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
          });
      } catch {
          return dateString;
      }
  };

  // --- Render ---
  if (loading) return <DashboardContainer>Loading payments...</DashboardContainer>;
  if (error) return <DashboardContainer style={{ color: 'red' }}>Error: {error}</DashboardContainer>;

  return (
    <DashboardContainer>
      <Header>
        <Title>💳 Payment Records ({payments.length})</Title>
        {/* Add Search/Filter functionality here */}
      </Header>

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>ID (Snippet)</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Method</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(payment => (
              <tr key={payment.id}>
                <td>{payment.id.substring(0, 10)}...</td>
                <td>{payment.currency} {payment.amount?.toLocaleString() || 'N/A'}</td>
                <td>
                  <StatusBadge status={payment.payment_status}>
                    <FontAwesomeIcon icon={getStatusIcon(payment.payment_status)} style={{ marginRight: 6 }} />
                    {payment.payment_status}
                  </StatusBadge>
                </td>
                <td>{formatDate(payment.created_date)}</td>
                <td>{payment.payment_method}</td>
                <td>
                  <ActionButton onClick={() => setSelectedPayment(payment)}>
                    <FontAwesomeIcon icon={faInfoCircle} /> View
                  </ActionButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      {/* Payment Detail Modal */}
      {selectedPayment && (
        <ModalOverlay onClick={() => setSelectedPayment(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <Title>Payment Details</Title>
            <hr />
            <DetailItem><strong>Document ID:</strong> {selectedPayment.id}</DetailItem>
            <DetailItem><strong>Order ID:</strong> {selectedPayment.order_tracking_id}</DetailItem>
            <DetailItem>
                <strong>Amount Paid:</strong> 
                <span style={{ color: '#007bff', fontWeight: 'bold' }}>
                    {selectedPayment.currency} {selectedPayment.amount.toLocaleString()}
                </span>
            </DetailItem>
            <DetailItem>
                <strong>Status:</strong> 
                <StatusBadge status={selectedPayment.payment_status}>{selectedPayment.payment_status}</StatusBadge>
            </DetailItem>
            <DetailItem><strong>Confirmation Code:</strong> {selectedPayment.confirmation_code}</DetailItem>
            <DetailItem><strong>Payment Account:</strong> {selectedPayment.payment_account}</DetailItem>
            <DetailItem><strong>Payment Method:</strong> {selectedPayment.payment_method}</DetailItem>
            <DetailItem><strong>Merchant Reference:</strong> {selectedPayment.merchant_reference}</DetailItem>
            <DetailItem><strong>Created Date:</strong> {formatDate(selectedPayment.created_date)}</DetailItem>
            <DetailItem><strong>Verified At:</strong> {formatDate(selectedPayment.verified_at)}</DetailItem>
            <DetailItem><strong>Status Code:</strong> {selectedPayment.status_code}</DetailItem>
            <ActionButton onClick={() => setSelectedPayment(null)} style={{ marginTop: 20, float: 'right' }}>
                Close
            </ActionButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardContainer>
  );
};